/**
 * useConversationActions Hook
 *
 * Handles actions on conversations: archive, flag, assign, unsubscribe, etc.
 * Now uses the new /api/v1/conversations/:id/flags endpoint.
 */

import {useCallback} from 'react'
import {toast} from 'react-toastify'
import {ConversationData} from '../types/inbox-v3.types'
import {
  useUpdateConversationFlagsMutation,
  useBulkUpdateConversationFlagsMutation,
  useUnSubscribeContactMutation,
  useSubscribeContactMutation,
} from '../../../../../services/cellcast'
import type {ConversationFlagUpdates} from '../../../../../services/cellcast.conversations.types'

interface UseConversationActionsOptions {
  onActionComplete?: () => void
}

interface UseConversationActionsReturn {
  // Single conversation actions
  archive: (conversation: ConversationData) => Promise<void>
  unarchive: (conversation: ConversationData) => Promise<void>
  toggleArchive: (conversation: ConversationData) => Promise<void>
  flag: (conversation: ConversationData) => Promise<void>
  unflag: (conversation: ConversationData) => Promise<void>
  toggleFlag: (conversation: ConversationData) => Promise<void>
  tick: (conversation: ConversationData) => Promise<void>
  untick: (conversation: ConversationData) => Promise<void>
  toggleTick: (conversation: ConversationData) => Promise<void>
  markAsRead: (conversation: ConversationData) => Promise<void>
  markAsUnread: (conversation: ConversationData) => Promise<void>
  assign: (conversation: ConversationData, userId: string) => Promise<void>
  unassign: (conversation: ConversationData) => Promise<void>

  // Subscribe/Unsubscribe actions (uses contact ID from conversation)
  subscribe: (conversation: ConversationData) => Promise<void>
  unsubscribe: (conversation: ConversationData) => Promise<void>

  // Bulk actions
  bulkArchive: (conversationIds: string[]) => Promise<void>
  bulkUnarchive: (conversationIds: string[]) => Promise<void>
  bulkFlag: (conversationIds: string[]) => Promise<void>
  bulkUnflag: (conversationIds: string[]) => Promise<void>
  bulkMarkAsRead: (conversationIds: string[]) => Promise<void>
  bulkSubscribe: (conversations: ConversationData[]) => Promise<void>
  bulkUnsubscribe: (conversations: ConversationData[]) => Promise<void>

  // Loading state
  isLoading: boolean
}

export function useConversationActions(
  options: UseConversationActionsOptions = {}
): UseConversationActionsReturn {
  const {onActionComplete} = options

  // RTK Query mutations for flags
  const [updateFlags, {isLoading: isUpdating}] = useUpdateConversationFlagsMutation()
  const [bulkUpdateFlags, {isLoading: isBulkUpdating}] = useBulkUpdateConversationFlagsMutation()

  // RTK Query mutations for subscribe/unsubscribe (uses existing inbox-v2 API)
  const [unsubscribeContact, {isLoading: isUnsubscribing}] = useUnSubscribeContactMutation()
  const [subscribeContact, {isLoading: isSubscribing}] = useSubscribeContactMutation()

  // ============================================================================
  // Helper Functions
  // ============================================================================

  /**
   * Update flags for a single conversation
   */
  const updateConversationFlags = useCallback(
    async (conversationId: string, flags: ConversationFlagUpdates) => {
      try {
        await updateFlags({conversationId, flags}).unwrap()
        onActionComplete?.()
      } catch (error) {
        console.error('Failed to update conversation flags:', error)
        throw error
      }
    },
    [updateFlags, onActionComplete]
  )

  /**
   * Update flags for multiple conversations
   */
  const updateBulkFlags = useCallback(
    async (conversationIds: string[], flags: ConversationFlagUpdates) => {
      try {
        await bulkUpdateFlags({conversationIds, flags}).unwrap()
        onActionComplete?.()
      } catch (error) {
        console.error('Failed to bulk update conversation flags:', error)
        throw error
      }
    },
    [bulkUpdateFlags, onActionComplete]
  )

  // ============================================================================
  // Single Conversation Actions
  // ============================================================================

  // Archive actions
  const archive = useCallback(
    async (conversation: ConversationData) => {
      await updateConversationFlags(conversation._id, {isArchived: true})
    },
    [updateConversationFlags]
  )

  const unarchive = useCallback(
    async (conversation: ConversationData) => {
      await updateConversationFlags(conversation._id, {isArchived: false})
    },
    [updateConversationFlags]
  )

  const toggleArchive = useCallback(
    async (conversation: ConversationData) => {
      const isArchived = !conversation.contacts.isArchived
      try {
        await updateConversationFlags(conversation._id, {isArchived})
        toast.success(isArchived ? 'Conversation archived' : 'Conversation unarchived')
      } catch (error) {
        toast.error('Failed to update archive status')
      }
    },
    [updateConversationFlags]
  )

  // Flag actions
  const flag = useCallback(
    async (conversation: ConversationData) => {
      await updateConversationFlags(conversation._id, {isFlagged: true})
    },
    [updateConversationFlags]
  )

  const unflag = useCallback(
    async (conversation: ConversationData) => {
      await updateConversationFlags(conversation._id, {isFlagged: false})
    },
    [updateConversationFlags]
  )

  const toggleFlag = useCallback(
    async (conversation: ConversationData) => {
      const isFlagged = !conversation.contacts.isFlagged
      try {
        await updateConversationFlags(conversation._id, {isFlagged})
        toast.success(isFlagged ? 'Conversation flagged' : 'Conversation unflagged')
      } catch (error) {
        toast.error('Failed to update flag status')
      }
    },
    [updateConversationFlags]
  )

  // Tick actions (using isClosed as equivalent)
  const tick = useCallback(
    async (conversation: ConversationData) => {
      await updateConversationFlags(conversation._id, {isClosed: true})
    },
    [updateConversationFlags]
  )

  const untick = useCallback(
    async (conversation: ConversationData) => {
      await updateConversationFlags(conversation._id, {isClosed: false})
    },
    [updateConversationFlags]
  )

  const toggleTick = useCallback(
    async (conversation: ConversationData) => {
      const isClosed = !conversation.contacts.isTicked
      await updateConversationFlags(conversation._id, {isClosed})
    },
    [updateConversationFlags]
  )

  // Read status actions
  const markAsRead = useCallback(
    async (conversation: ConversationData) => {
      await updateConversationFlags(conversation._id, {isRead: true})
    },
    [updateConversationFlags]
  )

  const markAsUnread = useCallback(
    async (conversation: ConversationData) => {
      await updateConversationFlags(conversation._id, {isRead: false})
    },
    [updateConversationFlags]
  )

  // Assignment actions
  const assign = useCallback(
    async (conversation: ConversationData, userId: string) => {
      await updateConversationFlags(conversation._id, {assignedTo: userId})
    },
    [updateConversationFlags]
  )

  const unassign = useCallback(
    async (conversation: ConversationData) => {
      await updateConversationFlags(conversation._id, {assignedTo: null})
    },
    [updateConversationFlags]
  )

  // ============================================================================
  // Subscribe/Unsubscribe Actions (uses existing inbox-v2 API)
  // ============================================================================

  /**
   * Unsubscribe a contact from receiving messages
   * Uses the contact ID from the conversation
   */
  const unsubscribe = useCallback(
    async (conversation: ConversationData) => {
      const contactId = conversation.contacts._id
      if (!contactId) {
        console.error('Cannot unsubscribe: No contact ID found')
        toast.error('Cannot unsubscribe: Contact not found')
        return
      }

      try {
        await unsubscribeContact({contactIDs: [contactId]}).unwrap()
        toast.success('Contact unsubscribed successfully')
        onActionComplete?.()
      } catch (error) {
        console.error('Failed to unsubscribe contact:', error)
        toast.error('Failed to unsubscribe contact')
        throw error
      }
    },
    [unsubscribeContact, onActionComplete]
  )

  /**
   * Subscribe a contact to receive messages again
   * Uses the contact ID from the conversation
   */
  const subscribe = useCallback(
    async (conversation: ConversationData) => {
      const contactId = conversation.contacts._id
      if (!contactId) {
        console.error('Cannot subscribe: No contact ID found')
        toast.error('Cannot subscribe: Contact not found')
        return
      }

      try {
        await subscribeContact({contactIDs: [contactId]}).unwrap()
        toast.success('Contact subscribed successfully')
        onActionComplete?.()
      } catch (error) {
        console.error('Failed to subscribe contact:', error)
        toast.error('Failed to subscribe contact')
        throw error
      }
    },
    [subscribeContact, onActionComplete]
  )

  // ============================================================================
  // Bulk Actions
  // ============================================================================

  const bulkArchive = useCallback(
    async (conversationIds: string[]) => {
      await updateBulkFlags(conversationIds, {isArchived: true})
    },
    [updateBulkFlags]
  )

  const bulkUnarchive = useCallback(
    async (conversationIds: string[]) => {
      await updateBulkFlags(conversationIds, {isArchived: false})
    },
    [updateBulkFlags]
  )

  const bulkFlag = useCallback(
    async (conversationIds: string[]) => {
      await updateBulkFlags(conversationIds, {isFlagged: true})
    },
    [updateBulkFlags]
  )

  const bulkUnflag = useCallback(
    async (conversationIds: string[]) => {
      await updateBulkFlags(conversationIds, {isFlagged: false})
    },
    [updateBulkFlags]
  )

  const bulkMarkAsRead = useCallback(
    async (conversationIds: string[]) => {
      await updateBulkFlags(conversationIds, {isRead: true})
    },
    [updateBulkFlags]
  )

  /**
   * Bulk unsubscribe multiple contacts
   */
  const bulkUnsubscribe = useCallback(
    async (conversations: ConversationData[]) => {
      const contactIds = conversations
        .map((c) => c.contacts._id)
        .filter((id): id is string => Boolean(id))

      if (contactIds.length === 0) {
        toast.error('No valid contacts to unsubscribe')
        return
      }

      try {
        await unsubscribeContact({contactIDs: contactIds}).unwrap()
        toast.success(`${contactIds.length} contact(s) unsubscribed successfully`)
        onActionComplete?.()
      } catch (error) {
        console.error('Failed to bulk unsubscribe contacts:', error)
        toast.error('Failed to unsubscribe contacts')
        throw error
      }
    },
    [unsubscribeContact, onActionComplete]
  )

  /**
   * Bulk subscribe multiple contacts
   */
  const bulkSubscribe = useCallback(
    async (conversations: ConversationData[]) => {
      const contactIds = conversations
        .map((c) => c.contacts._id)
        .filter((id): id is string => Boolean(id))

      if (contactIds.length === 0) {
        toast.error('No valid contacts to subscribe')
        return
      }

      try {
        await subscribeContact({contactIDs: contactIds}).unwrap()
        toast.success(`${contactIds.length} contact(s) subscribed successfully`)
        onActionComplete?.()
      } catch (error) {
        console.error('Failed to bulk subscribe contacts:', error)
        toast.error('Failed to subscribe contacts')
        throw error
      }
    },
    [subscribeContact, onActionComplete]
  )

  return {
    // Single conversation actions
    archive,
    unarchive,
    toggleArchive,
    flag,
    unflag,
    toggleFlag,
    tick,
    untick,
    toggleTick,
    markAsRead,
    markAsUnread,
    assign,
    unassign,
    subscribe,
    unsubscribe,

    // Bulk actions
    bulkArchive,
    bulkUnarchive,
    bulkFlag,
    bulkUnflag,
    bulkMarkAsRead,
    bulkSubscribe,
    bulkUnsubscribe,

    // Loading state
    isLoading: isUpdating || isBulkUpdating || isSubscribing || isUnsubscribing,
  }
}